import type { AggregatedOrderView } from '@/types/api/aggregated/order'
import type { AccountConfigurationView } from '@/types/api/investment-advisor-representative'

type PickConfigValue<T> = (config: AccountConfigurationView) => T | undefined

function pickFrom<T>(
  config: AccountConfigurationView | undefined,
  pick: PickConfigValue<T>,
): T | undefined {
  return config ? pick(config) : undefined
}

/**
 * One value per allocation record of the order: per account request (falling
 * back to the matching allocation_info entry), else per allocation_info entry,
 * else one for the order's single account. Index-aligned with the account rows
 * of the allocations export.
 */
export function listAllocationRecordValues<T>(
  order: AggregatedOrderView,
  pick: PickConfigValue<T>,
): Array<T | undefined> {
  if (order.account_requests?.length) {
    return order.account_requests.map(
      (request) =>
        pickFrom(request.account_configuration, pick) ??
        pickFrom(
          order.allocation_info?.find(
            (allocation) => allocation.account_id === request.account_id,
          )?.account_configuration,
          pick,
        ),
    )
  }
  if (order.allocation_info?.length) {
    return order.allocation_info.map((allocation) =>
      pickFrom(allocation.account_configuration, pick),
    )
  }
  return [pickFrom(order.account_configuration, pick)]
}
